/** @typedef {[number, number]} Point */

import { MIN_WALL_LENGTH_METERS } from './floorPlanDesignerState.js';

const MIN_POLYGON_AREA = 1e-2;

/**
 * @param {Point[]} points
 * @returns {number}
 */
export function polygonArea(points) {
  if (points.length < 3) return 0;
  let sum = 0;
  for (let i = 0; i < points.length; i += 1) {
    const j = (i + 1) % points.length;
    sum += points[i][0] * points[j][1] - points[j][0] * points[i][1];
  }
  return sum / 2;
}

/**
 * @param {Point} a
 * @param {Point} b
 * @param {Point} c
 * @param {Point} d
 * @returns {number}
 */
function cross2d(a, b, c, d) {
  return (b[0] - a[0]) * (d[1] - c[1]) - (b[1] - a[1]) * (d[0] - c[0]);
}

/**
 * @param {Point} a
 * @param {Point} b
 * @param {Point} c
 * @param {Point} d
 * @returns {boolean}
 */
function segmentsIntersectProper(a, b, c, d) {
  const d1 = cross2d(a, b, a, c);
  const d2 = cross2d(a, b, a, d);
  const d3 = cross2d(c, d, c, a);
  const d4 = cross2d(c, d, c, b);
  if (((d1 > 0 && d2 < 0) || (d1 < 0 && d2 > 0)) && ((d3 > 0 && d4 < 0) || (d3 < 0 && d4 > 0))) {
    return true;
  }
  return false;
}

/**
 * @param {Point[]} points
 * @returns {boolean}
 */
export function isPolygonSelfIntersecting(points) {
  const n = points.length;
  if (n < 4) return false;
  for (let i = 0; i < n; i += 1) {
    const a = points[i];
    const b = points[(i + 1) % n];
    for (let j = i + 2; j < n; j += 1) {
      if (i === 0 && j === n - 1) continue;
      const c = points[j];
      const d = points[(j + 1) % n];
      if (segmentsIntersectProper(a, b, c, d)) return true;
    }
  }
  return false;
}

/**
 * @param {unknown} value
 * @returns {value is number}
 */
function isFiniteNumber(value) {
  return typeof value === 'number' && Number.isFinite(value);
}

/**
 * @param {Point} point
 * @returns {boolean}
 */
function isFinitePoint(point) {
  return (
    Array.isArray(point) && point.length >= 2 && isFiniteNumber(point[0]) && isFiniteNumber(point[1])
  );
}

/**
 * @param {Point[]} polygon
 * @returns {boolean}
 */
export function arePolygonPointsFinite(polygon) {
  if (!polygon || polygon.length !== 4) return false;
  return polygon.every((p) => isFinitePoint(p));
}

/**
 * @param {Point} start
 * @param {Point} end
 * @returns {boolean}
 */
export function arePivotPointsFinite(start, end) {
  return isFinitePoint(start) && isFinitePoint(end);
}

/** @deprecated Use arePivotPointsFinite */
export const areCenterlinePointsFinite = arePivotPointsFinite;

/**
 * @param {Point} start
 * @param {Point} end
 * @param {number} pixelsPerMeter
 * @returns {boolean}
 */
export function isPivotEdgeLongEnough(start, end, pixelsPerMeter) {
  const ppm = pixelsPerMeter > 0 ? pixelsPerMeter : 100;
  const lenPx = Math.hypot(end[0] - start[0], end[1] - start[1]);
  return lenPx >= MIN_WALL_LENGTH_METERS * ppm;
}

/** @deprecated Use isPivotEdgeLongEnough */
export const isCenterlineLongEnough = isPivotEdgeLongEnough;

export { MIN_POLYGON_AREA };
